import React from 'react';
import PropTypes from 'prop-types';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    Legend,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import { Spacer } from '@zillow/constellation';

import GraphConstants from 'app/shared/constants/GraphConstants';
import GraphEmptyMessage from 'app/shared/components/Graphs/GraphEmptyMessage';
import GraphTitle from 'app/shared/components/Graphs/GraphTitle';
import GraphTotalTooltip from 'app/shared/components/Graphs/GraphTotalTooltip';
import numberUtils from 'app/shared/utils/numberUtils';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

const propTypes = {
    data: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
};

const defaultProps = {
    isLoading: false,
};

const GRAPH = GraphConstants.GRAPHS.INVOICE;
const coreProductBarKey = GraphConstants.GRAPH_DATA_TYPES.CORE_PRODUCTS;
const boostBarKey = GraphConstants.GRAPH_DATA_TYPES.BOOST;
const rocketshipBarKey = GraphConstants.GRAPH_DATA_TYPES.ROCKETSHIP;
const singleFamilyLegacyBarKey = GraphConstants.GRAPH_DATA_TYPES.SINGLE_FAMILY_LEGACY;
const leaseConnectBarKey = GraphConstants.GRAPH_DATA_TYPES.LEASE_CONNECT;
const barKeys = [coreProductBarKey, boostBarKey, rocketshipBarKey, singleFamilyLegacyBarKey, leaseConnectBarKey];

const renderFormatter = value => numberUtils.asCurrency(value);

const InvoiceGraph = ({
    isLoading,
    separateLeaseConnect,
    data,
}) => {
    const configs = GRAPH.default;
    const currentData = isLoading ? 0 : data[configs.DATA_TYPE];
    const colors = Object.values(GraphConstants.COLORS.zillow);
    const totalsByBarKey = {};
    const colorsByBarKey = {};

    let barCount = 0;
    for (const barKey of barKeys) {
        if (barKey !== leaseConnectBarKey || separateLeaseConnect) {
            const total = parseFloat(currentData[barKey] || 0);
            totalsByBarKey[barKey] = total;
            if (total) {
                colorsByBarKey[barKey] = colors[barCount];
                // eslint-disable-next-line no-plusplus
                barCount++;
                if (barKey === leaseConnectBarKey) {
                    delete colorsByBarKey[coreProductBarKey];
                }
            }
        }
    }

    const renderGraph = () => (
        <div>
            {currentData.isEmpty && <GraphEmptyMessage message="No invoices available" />}
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={currentData.months} key={coreProductBarKey}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <Tooltip content={<GraphTotalTooltip />} formatter={renderFormatter} />
                    <Legend align="left" verticalAlign="bottom" wrapperStyle={{ marginLeft: 60 }} />
                    {Object.entries(colorsByBarKey).map(([barKey, color], index) => (
                        <Bar
                            key={`bar-${index}`}
                            dataKey={barKey}
                            stackId="a"
                            fill={color}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    const number = totalsByBarKey[coreProductBarKey]
        + totalsByBarKey[boostBarKey]
        + totalsByBarKey[rocketshipBarKey]
        + totalsByBarKey[singleFamilyLegacyBarKey];
    const title = `${configs.TITLE_PREFIX}: ${numberUtils.asCurrencyWithDefault(number, '-')}`;

    return (
        <Spacer marginY="md">
            <GraphTitle
                title={title}
                isLoading={isLoading}
            />
            {isLoading ? <SpinnerContainer /> : renderGraph()}
        </Spacer>
    );
};

InvoiceGraph.propTypes = propTypes;
InvoiceGraph.defaultProps = defaultProps;

export default InvoiceGraph;
